import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Phone, CheckCircle, Loader, Instagram, Facebook } from 'lucide-react';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useInstances } from '../contexts/InstancesContext';

const SelectAccount = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const { refreshInstances } = useInstances();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [accounts, setAccounts] = useState({ whatsapp: [], instagram: [] });
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [connectedAccounts, setConnectedAccounts] = useState(new Set());

  const accessToken = searchParams.get('access_token');
  const clienteId = searchParams.get('cliente_id') || user.cliente_id;

  useEffect(() => {
    if (!accessToken) {
      toast.error('Token de acesso não encontrado');
      navigate('/configuracoes/canais');
      return;
    }
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const [instagramRes, whatsappRes, instancesRes] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_API_URL}/meta-auth/list-accounts?access_token=${encodeURIComponent(accessToken)}&platform=instagram`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        ).catch(() => ({ json: async () => ({ data: [] }) })),
        fetch(
          `${process.env.REACT_APP_API_URL}/meta-auth/list-accounts?access_token=${encodeURIComponent(accessToken)}&platform=whatsapp`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        ).catch(() => ({ json: async () => ({ data: [] }) })),
        fetch(
          `${process.env.REACT_APP_API_URL}/instancias-whatsapp?cliente_id=${clienteId}`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        )
      ]);

      const instagramData = await instagramRes.json();
      const whatsappData = await whatsappRes.json();
      const instancesData = await instancesRes.json();

      const connected = new Set();
      instancesData.forEach(inst => {
        if (inst.cliente_id === clienteId && inst.status === 'conectada') {
          if (inst.instagram_account_id) connected.add(`ig:${inst.instagram_account_id}`);
          if (inst.page_id && inst.platform === 'facebook') connected.add(`fb:${inst.page_id}`);
          if (inst.phone_number_id) connected.add(`wa:${inst.phone_number_id}`);
        }
      });

      setConnectedAccounts(connected);
      setAccounts({
        whatsapp: whatsappData.data || [],
        instagram: instagramData.data || [],
      });
    } catch (error) {
      toast.error('Erro ao carregar contas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAccount = (key, isConnected) => {
    if (isConnected) return;
    setSelectedAccounts(prev => 
      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
    );
  };

  const handleConnectSelected = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Selecione pelo menos uma conta');
      return;
    }

    setConnecting(true);
    try {
      const promises = selectedAccounts.map(key => {
        if (key.startsWith('wa:')) {
          const [, wabaId, phoneId] = key.split(':');
          return fetch(
            `${process.env.REACT_APP_API_URL}/meta-auth/connect-account?cliente_id=${clienteId}&waba_id=${wabaId}&phone_id=${phoneId}&access_token=${encodeURIComponent(accessToken)}&usuario_id=${user.id}&redirect=false`,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
          ).then(r => r.json());
        } else if (key.startsWith('ig:')) {
          const instagramId = key.substring(3);
          return fetch(
            `${process.env.REACT_APP_API_URL}/meta-auth/connect-instagram-account?cliente_id=${clienteId}&instagram_id=${instagramId}&access_token=${encodeURIComponent(accessToken)}&usuario_id=${user.id}&redirect=false`,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
          ).then(r => r.json());
        } else {
          const pageId = key.substring(3);
          const page = accounts.instagram.find(p => p.page_id === pageId);
          return fetch(
            `${process.env.REACT_APP_API_URL}/meta-auth/connect-facebook-account?cliente_id=${clienteId}&page_id=${pageId}&page_name=${encodeURIComponent(page?.page_name || 'Facebook')}&access_token=${encodeURIComponent(accessToken)}&usuario_id=${user.id}&redirect=false`,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
          ).then(r => r.json());
        }
      });

      const results = await Promise.all(promises);
      const success = results.filter(r => r.success).length;
      const errors = results.filter(r => !r.success);
      
      if (errors.length > 0) {
        const msg = errors[0]?.error || errors[0]?.message || 'Erro ao conectar';
        toast.error(msg);
      }

      if (success > 0) {
        toast.success(`${success} conta(s) conectada(s) com sucesso!`);
        await refreshInstances(); // Atualizar lista de instâncias
        navigate('/configuracoes/canais');
      } else {
        toast.error('Erro ao conectar contas');
        setConnecting(false);
      }
    } catch (error) {
      toast.error('Erro ao conectar contas');
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  const hasWhatsApp = accounts.whatsapp?.length > 0;
  const hasInstagram = accounts.instagram?.length > 0;
  const hasAny = hasWhatsApp || hasInstagram;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Selecione suas Contas Meta
          </h1>
          <p className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Escolha quais contas você deseja conectar (Instagram ou Facebook)
          </p>
        </div>

        <div className="space-y-6">
          {/* WhatsApp */}
          {hasWhatsApp && (
            <div>
              <h2 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <Phone className="w-5 h-5 text-green-500" />
                WhatsApp Business
              </h2>
              <div className="space-y-4">
                {accounts.whatsapp.map((account) => (
                  <div key={account.waba_id} className={`p-4 rounded-xl border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {account.waba_name} • {account.business_name}
                    </p>
                    <div className="space-y-2">
                      {account.phones.map((phone) => {
                        const key = `wa:${account.waba_id}:${phone.id}`;
                        const isConnected = connectedAccounts.has(`wa:${phone.id}`);
                        const isSelected = selectedAccounts.includes(key);
                        
                        return (
                          <div
                            key={phone.id}
                            onClick={() => toggleAccount(key, isConnected)}
                            className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                              isConnected
                                ? 'border-gray-400 bg-gray-100 dark:bg-gray-800 opacity-60 cursor-not-allowed'
                                : isSelected
                                ? 'border-green-500 bg-green-50 dark:bg-green-900/20 cursor-pointer'
                                : 'border-gray-300 dark:border-gray-600 hover:border-green-400 cursor-pointer'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isConnected ? 'bg-gray-400 border-gray-400' : isSelected ? 'bg-green-500 border-green-500' : 'border-gray-400'
                              }`}>
                                {(isSelected || isConnected) && <CheckCircle className="w-4 h-4 text-white" />}
                              </div>
                              <div>
                                <div className={`font-medium flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {phone.display_phone_number}
                                  {isConnected && <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-600">Conectado</span>}
                                </div>
                                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{phone.verified_name}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Instagram/Facebook */}
          {hasInstagram && (
            <div>
              <h2 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <Instagram className="w-5 h-5 text-purple-500" />
                Instagram & Facebook
              </h2>
              <div className="space-y-2">
                {accounts.instagram.map((page) => {
                  // Se tem Instagram, criar 2 opções: Instagram + Facebook
                  if (page.has_instagram) {
                    const igKey = `ig:${page.instagram_account.id}`;
                    const fbKey = `fb:${page.page_id}`;
                    const igConnected = connectedAccounts.has(igKey);
                    const fbConnected = connectedAccounts.has(fbKey);
                    const igSelected = selectedAccounts.includes(igKey);
                    const fbSelected = selectedAccounts.includes(fbKey);
                    
                    return (
                      <div key={page.page_id} className={`p-3 rounded-xl border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                        <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {page.page_name}
                        </p>
                        <div className="space-y-2">
                          {/* Instagram */}
                          <div
                            onClick={() => toggleAccount(igKey, igConnected)}
                            className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                              igConnected
                                ? 'border-gray-400 bg-gray-100 dark:bg-gray-800 opacity-60 cursor-not-allowed'
                                : igSelected
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 cursor-pointer'
                                : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 cursor-pointer'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                igConnected ? 'bg-gray-400 border-gray-400' : igSelected ? 'bg-purple-500 border-purple-500' : 'border-gray-400'
                              }`}>
                                {(igSelected || igConnected) && <CheckCircle className="w-4 h-4 text-white" />}
                              </div>
                              <img src={page.instagram_account.profile_picture_url} alt="" className="w-8 h-8 rounded-full" />
                              <div>
                                <div className={`font-medium flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  @{page.instagram_account.username}
                                  {igConnected && <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-600">Conectado</span>}
                                </div>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {page.instagram_account.followers_count?.toLocaleString()} seguidores
                                </div>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${
                              igConnected ? 'bg-gray-500/20 text-gray-500' : 'bg-purple-500/20 text-purple-400'
                            }`}>
                              Instagram
                            </span>
                          </div>
                          
                          {/* Facebook */}
                          <div
                            onClick={() => toggleAccount(fbKey, fbConnected)}
                            className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                              fbConnected
                                ? 'border-gray-400 bg-gray-100 dark:bg-gray-800 opacity-60 cursor-not-allowed'
                                : fbSelected
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-pointer'
                                : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 cursor-pointer'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                fbConnected ? 'bg-gray-400 border-gray-400' : fbSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-400'
                              }`}>
                                {(fbSelected || fbConnected) && <CheckCircle className="w-4 h-4 text-white" />}
                              </div>
                              <Facebook className={`w-8 h-8 ${fbConnected ? 'text-gray-400' : 'text-blue-500'}`} />
                              <div>
                                <div className={`font-medium flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {page.page_name}
                                  {fbConnected && <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-600">Conectado</span>}
                                </div>
                                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  Messenger
                                </div>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${
                              fbConnected ? 'bg-gray-500/20 text-gray-500' : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              Facebook
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    // Página só Facebook
                    const fbKey = `fb:${page.page_id}`;
                    const fbConnected = connectedAccounts.has(fbKey);
                    const fbSelected = selectedAccounts.includes(fbKey);
                    
                    return (
                      <div
                        key={page.page_id}
                        onClick={() => toggleAccount(fbKey, fbConnected)}
                        className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                          fbConnected
                            ? 'border-gray-400 bg-gray-100 dark:bg-gray-800 opacity-60 cursor-not-allowed'
                            : fbSelected
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-pointer'
                            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            fbConnected ? 'bg-gray-400 border-gray-400' : fbSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-400'
                          }`}>
                            {(fbSelected || fbConnected) && <CheckCircle className="w-4 h-4 text-white" />}
                          </div>
                          <Facebook className={`w-5 h-5 ${fbConnected ? 'text-gray-400' : 'text-blue-500'}`} />
                          <div>
                            <div className={`font-medium flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {page.page_name}
                              {fbConnected && <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-600">Conectado</span>}
                            </div>
                            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {page.page_category}
                            </div>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          fbConnected ? 'bg-gray-500/20 text-gray-500' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          Facebook
                        </span>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          )}

          {!hasAny && (
            <div className={`text-center py-12 rounded-xl border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <Phone className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Nenhuma conta encontrada
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center gap-3">
          {selectedAccounts.length > 0 ? (
            <button
              onClick={handleConnectSelected}
              disabled={connecting}
              className="px-6 py-3 rounded-lg font-medium flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {connecting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Conectando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Conectar ({selectedAccounts.length})
                </>
              )}
            </button>
          ) : (
            <button
              onClick={() => navigate('/configuracoes/canais')}
              className="px-6 py-3 rounded-lg font-medium bg-blue-600 hover:bg-blue-700 text-white"
            >
              Concluir
            </button>
          )}
          <button
            onClick={() => navigate('/configuracoes/canais')}
            className={`px-4 py-3 rounded-lg ${
              darkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
            }`}
          >
            {selectedAccounts.length > 0 ? 'Cancelar' : 'Voltar'}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default SelectAccount;
